import { clientConfig } from '@/config/clientConfig';
import { PublicKey } from '@/shared/lib/secure-json/core/crypto-core/types';
import { SecureJsonBase } from '@/shared/lib/secure-json/core/secure-json-base';
import {
  CollectionItem,
  Key,
  Permission,
} from '@/shared/lib/secure-json/core/secure-json-collection/types';
import { CollectionName } from '@/shared/lib/sj-orm/constants';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { log } from '@/shared/utils/log';
import { windowEvents } from '@/shared/utils/windowEvents';

import { CollectionOperation, OperationType } from '../stores/secureJsonCollections.store';
import { CollectionToDtoMap } from '../types';

import { ISjCollection } from './types';

export class ExportedModeWriteError extends Error {
  constructor(message: string, collection: CollectionName) {
    super(message);
    this.name = 'ExportedModeWriteError';
    windowEvents.dispatchCustom('exported:sjWriteError', { collection });
  }
}

/**
 * SJCollection
 * - throwIfExportedMode
 * - commitCollection
 */
export class SJCollection<
  C extends CollectionName,
  TDto extends CollectionToDtoMap[C] = CollectionToDtoMap[C],
> implements ISjCollection<C, TDto>
{
  constructor(
    private readonly collectionName: C,
    private readonly secureJsonBase: SecureJsonBase,
    private readonly onChange: (
      name: CollectionName,
      collection: SecureJsonBase,
      operation: CollectionOperation,
    ) => void,
  ) {}

  get(id: DtoId): CollectionItem | undefined {
    return this.secureJsonBase.items.get(id);
  }

  count(): number {
    return this.secureJsonBase.items.size;
  }

  /**
   * @deprecated Use count
   */
  size(): number {
    return this.count();
  }

  async create(
    value: TDto,
    publicKeys: Array<PublicKey>,
    permissions?: Map<PublicKey, Set<Permission>>,
  ): Promise<TDto> {
    this.throwIfExportedMode();
    value.createdAt = new Date();
    value.updatedAt = new Date();
    await this.secureJsonBase.set({
      key: value.id,
      data: value,
      publicKeys,
      permissions,
    });
    this.commitCollection(OperationType.ADD, this.get(value.id)!);
    return value;
  }

  async update(value: TDto): Promise<TDto> {
    this.throwIfExportedMode();
    value.updatedAt = new Date();
    await this.secureJsonBase.set({
      key: value.id,
      data: value,
    });
    this.commitCollection(OperationType.CHANGE, this.get(value.id)!);
    return value;
  }

  async updateMany(values: TDto[]): Promise<TDto[]> {
    for await (const value of values) {
      await this.update(value);
    }
    return values;
  }

  findById(id: string): TDto | undefined {
    return this.secureJsonBase.items.get(id)?.decryptedData;
  }

  findOne(
    condition: (item: TDto) => boolean,
    // sort?: (a: T, b: T) => number,
    // skip?: number,
    // limit?: number,
  ): TDto | undefined {
    const collection = this.secureJsonBase.values().filter((value) => value.decryptedData);

    // if (sort) {
    //   collection = collection.sort((value1, value2) =>
    //     sort(value1.decryptedData, value2.decryptedData),
    //   );
    // }

    // if (skip) {
    //   collection = collection.slice(skip, collection.length);
    // }

    // if (limit) {
    //   collection = collection.slice(0, limit);
    // }

    const result = collection.find((value) => condition(value.decryptedData));

    return result?.decryptedData;
  }

  getAll(): TDto[] {
    return this.secureJsonBase.values().map((value) => value.decryptedData);
  }

  findMany(
    condition: (item: TDto) => boolean,
    sort?: (a: TDto, b: TDto) => number,
    skip?: number,
    limit?: number,
  ): TDto[] {
    // TODO decryptedData must exist ?!
    let collection = this.secureJsonBase.values().filter((value) => value.decryptedData);

    if (sort) {
      collection = collection.sort((value1, value2) =>
        sort(value1.decryptedData, value2.decryptedData),
      );
    }

    if (skip) {
      collection = collection.slice(skip, collection.length);
    }

    if (limit) {
      collection = collection.slice(0, limit);
    }

    return collection
      .filter((value) => condition(value.decryptedData))
      .map((value) => value.decryptedData);
  }

  /**@deprecated use removeById instead */
  remove(value: TDto): boolean {
    this.throwIfExportedMode();

    const item = this.get(value.id);

    if (item) {
      this.secureJsonBase.delete(value.id);
      this.commitCollection(OperationType.REMOVE, item);
    } else {
      log.warn(`[SJCollection] remove. item not found ${value.id}`);
    }

    return true;
  }

  removeById(id: string): boolean {
    this.throwIfExportedMode();

    const item = this.get(id);

    if (item) {
      this.secureJsonBase.delete(id);
      this.commitCollection(OperationType.REMOVE, item);
    } else {
      log.warn(`[SJCollection] remove. item not found id: ${id}`);
    }

    return true;
  }

  removeMany(condition: (item: TDto) => boolean): TDto[] {
    const values = this.findMany(condition);

    for (const value of values) {
      this.remove(value);
    }
    return values;
  }

  // Public keys
  public getPublicKeys(key: Key): PublicKey[] | undefined {
    return this.secureJsonBase.getPublicKeys(key);
  }

  public async putPublicKey(id: Key, publicKey: PublicKey): Promise<boolean> {
    this.throwIfExportedMode();
    await this.secureJsonBase.putPublicKey(id, publicKey);
    this.commitCollection(OperationType.CHANGE, this.get(id)!);
    return true;
  }

  public deletePublicKey(id: Key, publicKey: PublicKey): void {
    this.secureJsonBase.deletePublicKey(id, publicKey);
    this.commitCollection(OperationType.CHANGE, this.get(id)!);
  }

  // Permissions
  public getPermissions(key: Key): Map<PublicKey, Set<Permission>> | undefined {
    return this.secureJsonBase.getPermissions(key);
  }

  public putPermission(key: Key, publicKey: PublicKey, permission: Permission): void {
    this.secureJsonBase.putPermission(key, publicKey, permission);
    this.commitCollection(OperationType.CHANGE, this.get(key)!);
  }

  public putPermissions(key: Key, publicKey: PublicKey, permissions: Permission[]): void {
    this.secureJsonBase.putPermissions(key, publicKey, permissions);
    this.commitCollection(OperationType.CHANGE, this.get(key)!);
  }

  public deletePermission(key: Key, publicKey: PublicKey, permission: Permission): void {
    this.secureJsonBase.deletePermission(key, publicKey, permission);
    this.commitCollection(OperationType.CHANGE, this.get(key)!);
  }

  private throwIfExportedMode(): void {
    if (clientConfig.isExported) {
      throw new ExportedModeWriteError(
        `Collection ${this.collectionName} is protected in exported mode`,
        this.collectionName,
      );
    }
  }

  private commitCollection(operationType: OperationType, item: CollectionItem): void {
    this.throwIfExportedMode();

    const collectionName = this.collectionName;
    const operation = { operationType, collectionName, item };

    this.onChange(collectionName, this.secureJsonBase, operation);
  }
}
